import React, { useId, useRef } from 'react';
import styled from '@emotion/styled';
import { ActiveIndicator, IndicatorGroup } from '../../motion';
import {
  duration,
  easing,
  palette,
  radius,
  shadow,
  spacing,
  status,
  typography,
} from '../../style-utils/theme';

/* ───────────────────────────────────────────────────────────────────────────
 * MONTHLY / ANNUAL — the one switch every pricing surface shares
 *
 * A two-option radio group drawn as the product's segmented pill (the same
 * moving-indicator pattern as the shifts Live/History switch). It chooses WHICH
 * ROWS of the catalog are shown; it never computes a price. The saving on the
 * Annual option is the server's `annual_savings_badge_percent` — already
 * floored to a whole percent, so it can never overstate what annual saves.
 * Owner rule (migration 112): annual is twelve monthly payments less 15%,
 * rounded to the cent, so the server's badge reads 15. Never hard-code it.
 * ─────────────────────────────────────────────────────────────────────────── */

export type BillingInterval = 'month' | 'year';

interface IntervalToggleProps {
  value: BillingInterval;
  onChange: (next: BillingInterval) => void;
  /** Whole percent annual saves against monthly, from the server. */
  savingsPercent?: number | null;
  disabled?: boolean;
  /** Accessible name of the group. */
  label?: string;
}

const OPTIONS: { value: BillingInterval; label: string }[] = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Annual' },
];

export const IntervalToggle: React.FC<IntervalToggleProps> = ({
  value,
  onChange,
  savingsPercent = null,
  disabled = false,
  label = 'Billing period',
}) => {
  const uid = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  // Roving focus: arrows move AND select, like a native radio group.
  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const nextIndex = (index + 1) % OPTIONS.length;
    onChange(OPTIONS[nextIndex].value);
    refs.current[nextIndex]?.focus();
  };

  return (
    <IndicatorGroup id={`${uid}-interval`}>
      <Group role="radiogroup" aria-label={label}>
        {OPTIONS.map((option, index) => {
          const checked = option.value === value;

          return (
            <Option
              key={option.value}
              ref={el => {
                refs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              disabled={disabled}
              $checked={checked}
              onClick={() => onChange(option.value)}
              onKeyDown={event => onKeyDown(event, index)}
            >
              {checked && <Indicator id={`${uid}-interval-pill`} />}
              <OptionLabel>
                {option.label}
                {option.value === 'year' && savingsPercent != null && savingsPercent > 0 && (
                  <Saving>Save {savingsPercent}%</Saving>
                )}
              </OptionLabel>
            </Option>
          );
        })}
      </Group>
    </IndicatorGroup>
  );
};

const Group = styled.div`
  display: inline-flex;
  gap: ${spacing.half};
  padding: ${spacing.half};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.pill};
  align-self: flex-start;
  max-width: 100%;
`;

/* The label weight is held constant in both states — a 500->600 swap reflows
   the width the moving pill is sliding under. */
const Option = styled.button<{ $checked: boolean }>`
  position: relative;
  border: none;
  cursor: pointer;
  padding: ${spacing.xs} ${spacing.sm};
  min-height: 44px;
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${p => (p.$checked ? palette.surface : palette.slate)};
  background: transparent;
  transition: color ${duration.fast} ${easing.standard};

  &:hover:not(:disabled) {
    color: ${p => (p.$checked ? palette.surface : palette.ink)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const OptionLabel = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  white-space: nowrap;
`;

const Indicator = styled(ActiveIndicator)`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: ${radius.pill};
  background: ${palette.lagoon};
  box-shadow: ${shadow.xs};
`;

const Saving = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${status.success.bg};
  color: ${status.success.fg};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
  line-height: 20px;
`;
